import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore, CameraMode } from '../../../core/store/gameStore';

// ============================================================================
// useDroneCamera - a free-flying cinematic camera ("drone mode").
//
// The character stays planted while the camera roams the real Yosemite world
// on a full 3D flight rig. Hard collision is resolved as BLOCK + SLIDE:
//   - terrain / rising slopes act as solid ground (no clipping through rock)
//   - every tree is a solid vertical column (trunk + canopy) that ends at the
//     tree top, so trunks and crowns block, but the canopy layer can always be
//     flown OVER by climbing above the treetops (and you may rest on top)
//   - when an obstacle lies ahead the drone brakes, and blocked axes slip
//     around the obstacle instead of sticking to it
// Movement is keyboard translation + pointer-lock mouse look. Desktop only:
// mobile keeps using the touch rig and never enters this mode.
// ============================================================================

const CRUISE = 24;          // m/s forward/strafe
const CLIMB = 18;           // m/s vertical (Space up / Shift down)
const SPRINT_MULT = 3.2;    // hold R for a fast traverse
const ACCEL = 6.0;          // exponential approach rate (1/s)
const MOUSE_SENS = 0.0022;
const PITCH_LIMIT = Math.PI / 2 - 0.05;
const DRONE_R = 0.7;        // collision sphere around the eye
const GROUND_CLEAR = 1.2;   // keep the eye this high over terrain
const LIP = DRONE_R;        // terrain counts as solid this close under the eye
const MAX_STEP = 2.5;       // collision sub-step length (m)
const BRAKE_RANGE = 18;     // start braking this far from obstacles
const BRAKE_MIN = 0.35;     // minimum brake multiplier (still crawl)

interface DroneState {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  yaw: number;
  pitch: number;
}

interface Collider {
  x: number;
  y0: number;
  y1: number;
  r: number;
}

export function useDroneCamera({
  enabled,
}: {
  enabled: boolean;
}) {
  const { camera, gl } = useThree();
  const isMobile = useGameStore((state) => state.isMobile);

  const stateRef = useRef<DroneState | null>(null);
  const keysRef = useRef<Set<string>>(new Set());
  const colBuf = useRef<Collider[]>([]);

  // ---- keyboard ------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;
    const keys = keysRef.current;

    const onKeyDown = (e: KeyboardEvent) => {
      keys.add(e.code);
      if (e.code === 'Space') e.preventDefault();
    };
    const onKeyUp = (e: KeyboardEvent) => keys.delete(e.code);
    const onBlur = () => keys.clear();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      keys.clear();
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [enabled]);

  // ---- pointer lock mouse look --------------------------------------------
  useEffect(() => {
    if (!enabled || isMobile) return;
    const canvas = gl.domElement;

    const requestLock = async () => {
      if (document.pointerLockElement !== canvas) {
        try {
          await canvas.requestPointerLock();
        } catch {
          /* denied - the drone still flies on keyboard alone */
        }
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      const st = stateRef.current;
      if (!st || document.pointerLockElement !== canvas) return;
      st.yaw -= e.movementX * MOUSE_SENS;
      st.pitch -= e.movementY * MOUSE_SENS;
      st.pitch = THREE.MathUtils.clamp(st.pitch, -PITCH_LIMIT, PITCH_LIMIT);
    };

    const onClick = () => requestLock();
    canvas.addEventListener('click', onClick);
    document.addEventListener('mousemove', onMouseMove);

    requestLock();

    return () => {
      canvas.removeEventListener('click', onClick);
      document.removeEventListener('mousemove', onMouseMove);
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    };
  }, [enabled, isMobile, gl.domElement]);

  // ---- (re)seed the rig on each entry -------------------------------------
  useEffect(() => {
    if (!enabled || isMobile) {
      stateRef.current = null;
      return;
    }
    if (stateRef.current) return; // already seeded this entry

    const e = new THREE.Euler(0, 0, 0, 'YXZ');
    e.setFromQuaternion(camera.quaternion);
    stateRef.current = {
      pos: camera.position.clone(),
      vel: new THREE.Vector3(),
      yaw: e.y,
      pitch: THREE.MathUtils.clamp(e.x, -PITCH_LIMIT, PITCH_LIMIT),
    };
  }, [enabled, isMobile, camera]);

  useFrame((_, rawDelta) => {
    if (!enabled || isMobile) return;
    const st = stateRef.current;
    const store = useGameStore.getState();
    const terrain = store.terrain;
    const forest = store.forest;
    if (!st || !terrain || !forest) return;
    if (store.cameraMode !== CameraMode.Drone) return;

    const delta = Math.min(rawDelta, 0.05);
    const keys = keysRef.current;
    const sprint = keys.has('KeyR');
    const speed = CRUISE * (sprint ? SPRINT_MULT : 1);

    const mvF = (keys.has('KeyW') || keys.has('ArrowUp') ? 1 : 0)
              - (keys.has('KeyS') || keys.has('ArrowDown') ? 1 : 0);
    const mvS = (keys.has('KeyD') || keys.has('ArrowRight') ? 1 : 0)
              - (keys.has('KeyA') || keys.has('ArrowLeft') ? 1 : 0);
    const mvLen = Math.hypot(mvF, mvS);
    const inv = mvLen > 1 ? 1 / mvLen : 1;

    let climb = 0;
    if (keys.has('Space')) climb += 1;
    if (keys.has('ShiftLeft') || keys.has('ShiftRight')) climb -= 1;

    // Translation is yaw-relative only (ignores pitch): the aircraft glides
    // level while the pilot aims the lens freely with the mouse.
    const sinY = Math.sin(st.yaw);
    const cosY = Math.cos(st.yaw);
    const fwdX = -sinY, fwdZ = -cosY;
    const rightX = cosY, rightZ = -sinY;

    const tx = (fwdX * mvF + rightX * mvS) * speed * inv;
    const tz = (fwdZ * mvF + rightZ * mvS) * speed * inv;
    const ty = climb * CLIMB;

    // One obstacle sweep per frame; radius covers the whole step + brake zone.
    const lookAhead = speed * 0.6 + BRAKE_RANGE;
    const colliders = colBuf.current;
    const colCount = forest.collidersNear(st.pos.x, st.pos.z, lookAhead + 6, colliders, 0);

    // Brake the ground speed as trees/slopes approach on the heading. Vertical
    // speed is left to the hard clamps in integrate().
    const speed2D = Math.hypot(tx, tz);
    const brake = (speed2D > 1e-3)
      ? brakeFactor2D(st.pos, tx / speed2D, tz / speed2D, lookAhead, terrain, colliders, colCount, ty > 0)
      : 1;

    const k = 1 - Math.exp(-ACCEL * delta);
    st.vel.x += (tx * Math.max(BRAKE_MIN, brake) - st.vel.x) * k;
    st.vel.z += (tz * Math.max(BRAKE_MIN, brake) - st.vel.z) * k;
    st.vel.y += (ty - st.vel.y) * k;

    integrate(st, st.vel, delta, terrain, colliders, colCount);

    // Always end the frame in a safe spot, even when at rest (mode entry,
    // teleports, terrain streaming changes under a parked aircraft).
    const groundSafe = terrain.heightAt(st.pos.x, st.pos.z) + GROUND_CLEAR;
    if (st.pos.y < groundSafe) {
      st.pos.y = groundSafe;
      if (st.vel.y < 0) st.vel.y = 0;
    }
    for (let i = 0; i < colCount; i++) {
      const c = colliders[i];
      const dx = c.x - st.pos.x;
      const dz = c.z - st.pos.z;
      const rr = c.r + DRONE_R;
      if (dx * dx + dz * dz < rr * rr && st.pos.y < c.y1 + DRONE_R) {
        st.pos.y = c.y1 + DRONE_R;
        if (st.vel.y < 0) st.vel.y = 0;
      }
    }

    // Keep the aircraft over the dataset.
    const margin = 60;
    st.pos.x = THREE.MathUtils.clamp(st.pos.x, -terrain.halfW + margin, terrain.halfW - margin);
    st.pos.z = THREE.MathUtils.clamp(st.pos.z, -terrain.halfH + margin, terrain.halfH - margin);

    camera.position.copy(st.pos);
    camera.quaternion.setFromEuler(new THREE.Euler(st.pitch, st.yaw, 0, 'YXZ'));
    camera.updateMatrixWorld(true);
  });

  return null;
}

// ---------------------------------------------------------------------------
// 2D obstacle braking: reduce ground speed as the first tree/slope approaches
// along the intended heading. Returns 1 when the path is clear.
// ---------------------------------------------------------------------------
function brakeFactor2D(
  pos: THREE.Vector3,
  nx: number,
  nz: number,
  look: number,
  terrain: any,
  colliders: Collider[],
  count: number,
  climbing: boolean
): number {
  let minD = look;

  // Trees: distance from the eye to each column, projected on the heading.
  for (let i = 0; i < count; i++) {
    const c = colliders[i];
    if (pos.y + DRONE_R < c.y0 || pos.y - DRONE_R > c.y1) continue;
    const cx = c.x - pos.x;
    const cz = c.z - pos.z;
    const proj = cx * nx + cz * nz;
    if (proj <= 0) continue;
    const lat = Math.abs(cx * nz - cz * nx);
    const rr = c.r + DRONE_R;
    if (lat >= rr) continue;
    const along = proj - Math.sqrt(Math.max(rr * rr - lat * lat, 0));
    if (along < minD) minD = Math.max(along, 0);
  }

  // Terrain: a slope only counts when it reaches the aircraft altitude inside
  // the brake range.
  const samples = [3, 7, 12, look * 0.5, look];
  for (const d of samples) {
    const g = terrain.heightAt(pos.x + nx * d, pos.z + nz * d);
    if (g + LIP > pos.y) {
      const when = Math.max(d + (pos.y - (g + LIP)), 0);
      if (when < minD) minD = when;
      break;
    }
  }

  // A climbing pilot is already escaping the slope; ease braking.
  const eased = climbing ? minD + 8 : minD;
  return THREE.MathUtils.clamp(eased / BRAKE_RANGE, 0, 1);
}

// ---------------------------------------------------------------------------
// Sub-step movement, resolving X/Z independently: a blocked axis cancels while
// the free axis keeps going, which produces sliding along trunks and slopes.
// Vertical descent is hard-clamped by terrain and tree-top ceilings.
// ---------------------------------------------------------------------------
function integrate(
  st: DroneState,
  vel: THREE.Vector3,
  delta: number,
  terrain: any,
  colliders: Collider[],
  colCount: number
) {
  const dist = Math.hypot(vel.x * delta, vel.y * delta, vel.z * delta);
  if (dist < 1e-5) return;
  const steps = Math.max(1, Math.ceil(dist / MAX_STEP));
  const perX = (vel.x * delta) / steps;
  const perY = (vel.y * delta) / steps;
  const perZ = (vel.z * delta) / steps;

  for (let s = 0; s < steps; s++) {
    const p = st.pos;

    // Vertical first: a climbing pilot rides rising terrain instead of being
    // stopped by the slope, and descent lands on ground / treetops cleanly.
    const floorY = terrain.heightAt(p.x, p.z) + GROUND_CLEAR;
    let minY = floorY;
    for (let i = 0; i < colCount; i++) {
      const c = colliders[i];
      const dx = c.x - p.x;
      const dz = c.z - p.z;
      const rr = c.r + DRONE_R;
      if (dx * dx + dz * dz < rr * rr) {
        // Horizontally over this column: the eye must sit on or above its top.
        if (c.y1 + DRONE_R > minY) minY = c.y1 + DRONE_R;
      }
    }

    const yNext = p.y + perY;
    if (yNext < minY) {
      if (p.y > minY) {
        p.y = minY; // landed on ground / treetop
        if (vel.y < 0) vel.y = 0;
      } else {
        p.y = minY; // already too low (mode entry, teleport): pop to safety
      }
    } else {
      p.y = yNext;
    }

    // Horizontal, one axis at a time (slide behaviour at this altitude).
    const nx = p.x + perX;
    if (clear2D(nx, p.z, p.y, terrain, colliders, colCount)) p.x = nx;
    const nz = p.z + perZ;
    if (clear2D(p.x, nz, p.y, terrain, colliders, colCount)) p.z = nz;
  }
}

// Is the eye allowed to stand at this horizontal spot at this altitude?
function clear2D(
  x: number,
  z: number,
  y: number,
  terrain: any,
  colliders: Collider[],
  count: number
): boolean {
  // Ground / rising slope: never let the eye get closer than LIP to terrain.
  if (terrain.heightAt(x, z) + LIP > y) return false;

  // Tree columns (reuse the frame's sweep; the whole step is tiny).
  for (let i = 0; i < count; i++) {
    const c = colliders[i];
    if (y + DRONE_R < c.y0 || y - DRONE_R > c.y1) continue;
    const dx = c.x - x;
    const dz = c.z - z;
    const rr = c.r + DRONE_R;
    if (dx * dx + dz * dz < rr * rr) return false;
  }
  return true;
}
